/* engine/elevationProjection.js — the elevation cabinet drawing as a TRUE orthographic
 * projection of the 3D geometry (2026-09-12 user mandate: "3D geometry = Elevation geometry =
 * Dimensions … do NOT create approximate or hardcoded cabinet positions").
 *
 * WHAT THIS IS
 *   The 3D view builds every cabinet from cabinetPartsSpec() (scene3d/cabinetMath): world-space
 *   boxes for the carcass, door leaves on their hinge pivots, drawer fronts with their handles,
 *   side panels, kickboard, equipment, cut bands — and applyGolaRuns() adds the gola posts and
 *   rails as boxes of their own. This module takes THOSE SAME specs — nothing is re-derived from
 *   def/config here — and projects each part onto the wall plane the elevation is looking at.
 *   The result is a list of 2D primitives in metres (u along the wall from its start, v = height
 *   off the floor) with a depth key q (toward the room) for painter's order.
 *
 * WHY WORLD COORDINATES
 *   cabinetWorldPos() orients a cabinet by the ROOM-FACING normal (angle = atan2(nx, nz)), so a
 *   cabinet's local +x runs one way along a wall on one side of the room and the opposite way on
 *   the other. Reading local offsets would mirror hinges on half the walls. Projecting world
 *   points onto the wall's own axis ((P − start) · dir) is exact for every wall, every angle,
 *   every outset, and needs no sign convention — an angle-overridden module still projects
 *   correctly (its boxes foreshorten by |cos φ| / |sin φ|, exactly as an orthographic view would).
 *
 * HANDLES: the technician's exact-mm placement (engine/handlePlace) is applied with the SAME
 *   rect + key ("door-N"/"drawer-N" in spec order) the 3D mounts by, so a handle sits in the
 *   elevation where it sits in the room.
 *
 * PURE: no React, no THREE, no state mutation. Everything is unit-testable in mm.
 */
import { getWallPlacementInfo } from "./placement";
import { buildCabinetSpecs, applyGolaRuns } from "../components/scene3d/cabinetMath";
import { interiorVisuals } from "./interiorLayout";
import { effDef } from "./pricing";
import { handlePlaceFor, handlePlacePos } from "./handlePlace";
import { golaProfileById, golaProfileByName } from "./golaRuns";
import { resolveConfigVisual } from "../components/mcv3/configVisual";

/* ── the wall frame ─────────────────────────────────────────────────────── */

/** wallFrame(wall, walls) → { sx, sz, dirX, dirZ, nx, nz, len } | null.
 *  u = (P − start) · dir is exactly the axis ElevationView's ex() maps (absLeft space). */
export function wallFrame(wall, walls) {
  if (!wall || !wall.start || !wall.end) return null;
  const info = getWallPlacementInfo(wall, walls || []);
  if (!info) return null;
  return { sx: wall.start[0], sz: wall.start[1], dirX: info.dirX, dirZ: info.dirZ, nx: info.nx, nz: info.nz, len: info.len };
}

/** world (x,z) → { u, q }: along the wall from its start, and depth toward the room */
export function worldToWall(frame, x, z) {
  const dx = (x || 0) - frame.sx, dz = (z || 0) - frame.sz;
  return { u: dx * frame.dirX + dz * frame.dirZ, q: dx * frame.nx + dz * frame.nz };
}

/** the local-x and local-z axes of a part rotated by rotY, projected onto the wall axis.
 *  local x → world (cos a, −sin a); local z → world (sin a, cos a)   (cabinetMath _fxz) */
export function axesOnWall(frame, rotY) {
  const a = rotY || 0, ca = Math.cos(a), sa = Math.sin(a);
  return {
    ux: ca * frame.dirX - sa * frame.dirZ,      // how much local x runs along the wall (±1 on the wall)
    uz: sa * frame.dirX + ca * frame.dirZ,      // how much local z runs along the wall (0 on the wall)
    qx: ca * frame.nx - sa * frame.nz,          // …and toward the room
    qz: sa * frame.nx + ca * frame.nz,
  };
}

/** a world-space box (optionally stood up by rotZ, THREE order: rotZ first, then rotY) → its
 *  footprint on the wall plane. Exact for any rotation: an orthographic view of a box is the
 *  sum of its half-extents projected onto the view axes. */
export function projectBox(frame, part) {
  const { u, q } = worldToWall(frame, part.x, part.z);
  const ax = axesOnWall(frame, part.rotY);
  const rz = part.rotZ || 0, cz = Math.abs(Math.cos(rz)), sz = Math.abs(Math.sin(rz));
  const w = part.w || 0, h = part.h || 0, d = part.d || 0;
  const halfU = Math.abs(ax.ux) * (cz * w / 2 + sz * h / 2) + Math.abs(ax.uz) * d / 2;
  const halfV = sz * w / 2 + cz * h / 2;
  const halfQ = Math.abs(ax.qx) * (cz * w / 2 + sz * h / 2) + Math.abs(ax.qz) * d / 2;
  const y = part.y || 0;
  return { u0: u - halfU, u1: u + halfU, v0: y - halfV, v1: y + halfV, q: q + halfQ, qc: q };
}

/* ── the plinth zone: legs + a ghost plinth (2026-09-29) ─────────────────── */

/** LEG_W_M / LEG_IN_M — a levelling foot is drawn 50 mm wide, the outer ones 60 mm in from the
 *  carcass ends (the same inset the CV leveller glyph used); 2 feet up to 800 mm, 3 up to 1300,
 *  4 beyond, evenly spread. */
export const LEG_W_M = 0.05;
export const LEG_IN_M = 0.06;
/** LEG GEOMETRY (2026-09-30 user: "the legs are not really showing like the legs of the cabinets
 *  — CAD level"): a levelling foot drawn as the part it is, metres off the floor line, every
 *  width centred on the leg axis — the base plate on the floor, the adjuster collar above it,
 *  the threaded stem, the socket screwed under the carcass bottom, and the plinth-clip nose
 *  under the socket. A zone too short for the parts (< 30 mm) draws the stem alone. */
export const LEG = {
  base: { w: 0.050, h: 0.006 },
  collar: { w: 0.036, h: 0.010 },
  stem: { w: 0.026 },
  clip: { w: 0.030, h: 0.005 },
  socket: { w: 0.054, h: 0.012 },
};
export function legParts(cu, v0, v1) {
  const box = (name, w, a, b) => ({ name, u0: cu - w / 2, u1: cu + w / 2, v0: a, v1: b });
  if (!(v1 - v0 > 0.03)) return [box("stem", LEG.stem.w, v0, v1)];
  const baseTop = v0 + LEG.base.h, collarTop = baseTop + LEG.collar.h;
  const socketBot = v1 - LEG.socket.h, clipBot = socketBot - LEG.clip.h;
  return [
    box("stem", LEG.stem.w, collarTop, socketBot),
    box("base", LEG.base.w, v0, baseTop),
    box("collar", LEG.collar.w, baseTop, collarTop),
    box("clip", LEG.clip.w, clipBot, socketBot),
    box("socket", LEG.socket.w, socketBot, v1),
  ];
}
export function legPositions(u0, u1) {
  const w = u1 - u0;
  if (!(w > 0)) return [];
  const n = w <= 0.8 ? 2 : w <= 1.3 ? 3 : 4;
  const a = u0 + LEG_IN_M + LEG_W_M / 2, b = u1 - LEG_IN_M - LEG_W_M / 2;
  if (b <= a) return [(u0 + u1) / 2];
  const out = [];
  for (let i = 0; i < n; i++) out.push(a + (b - a) * (n === 1 ? 0.5 : i / (n - 1)));
  return out;
}

/* ── classification (which CSS class a part draws with) ─────────────────── */

const ROLE_CLASS = {
  carcass: "elev-carcass",
  kick: "elev-plinth-rect",
  leg: "elev-leg",
  sidePanel: "elev-side-panel",
  equipment: "elev-equipment",
  front: "elev-front-leaf",
  gola: "elev-gola-bar",
};

function classForBox(part, spec) {
  if (spec && spec.role === "filler") return "elev-filler";
  /* an appliance-role module (fridge, oven housing, hob…) reads as an appliance, not a carcass */
  if (spec && spec.role === "appliance" && part.role === "carcass") return "elev-equipment";
  return ROLE_CLASS[part.role] || "elev-carcass";
}

/** the tag a CAD elevation prints on an appliance-role module: its subtype, else its name */
export function applianceLabel(def, spec) {
  const s = String((def && def.subtype) || (spec && spec.name) || "").trim();
  return s ? s.toUpperCase().slice(0, 14) : "";
}

/** "Base 60 oven equipment" → "OVEN"; the label a CAD elevation prints inside an appliance opening */
export function equipmentLabel(name) {
  const m = /\s([a-z]+) equipment$/i.exec(name || "");
  return m ? m[1].toUpperCase() : "";
}

/** polygon-clipping MultiPolygon → flat [x, z] point list (rings nest 3 deep; be lenient) */
function flattenPoints(region, out) {
  if (!Array.isArray(region)) return out;
  if (region.length === 2 && typeof region[0] === "number" && typeof region[1] === "number") { out.push(region); return out; }
  region.forEach((r) => flattenPoints(r, out));
  return out;
}

/* ── one spec → primitives ──────────────────────────────────────────────── */

/**
 * projectSpec(frame, spec, opts) → primitives[] sorted back-to-front.
 *
 * Primitive shapes (all metres, u along the wall, v height):
 *   { kind: "rect",   cls, u0, u1, v0, v1, q, name, role, label? }
 *   { kind: "swing",  cls, hingeU, latchU, v0, v1, q }          — door swing "V", apex on the hinge
 *   { kind: "hidden", cls, u0, u1, v0, v1, q }                  — dashed, drawn last (interior)
 *   { kind: "gola",   cls, u0, u1, v0, v1, q, vertical }        — channel glyph (post or rail)
 *
 * `counter` parts are deliberately NOT emitted: the elevation draws the merged worktop RUNS from
 * engine/surfaces (thickness-true, seam-true) — the same oracle the 3D merges into. Drawer boxes
 * behind the fronts are not emitted either; they are invisible in a front view.
 *
 * opts: { def, config, blob, vis }  — vis = resolveConfigVisual(blob, type, config) for handles
 */
export function projectSpec(frame, spec, opts = {}) {
  const prims = [];
  if (!frame || !spec || !Array.isArray(spec.parts)) return prims;
  const push = (pr) => { pr.i = prims.length; prims.push(pr); };
  const fIdx = { door: 0, drawer: 0 };                       // "door-N"/"drawer-N" keys, spec order
  const vis = opts.vis || null;

  spec.parts.forEach((part) => {
    if (!part) return;

    if (part.kind === "box") {
      if (part.role === "counter") return;                     // the surfaces layer owns the slab
      const b = projectBox(frame, part);
      if (part.role === "gola") {
        const vertical = Math.abs(Math.sin(part.rotZ || 0)) > 0.5;
        push({ kind: "gola", cls: "elev-gola-bar", ...b, vertical, name: part.name, role: "gola" });
        return;
      }
      const cls = part.glass && part.role === "equipment" ? "elev-equip-glass" : classForBox(part, spec);
      const pr = { kind: "rect", cls, ...b, name: part.name || part.role, role: part.role };
      if (part.role === "equipment" && !part.glass) pr.label = equipmentLabel(part.name);
      else if (cls === "elev-equipment") pr.label = applianceLabel(opts.def, spec);
      /* a side panel rides its OWN finish on the part (cabinetMath: spPick / tex / sw + the
         photo's mm coverage) — the texture render mode paints it exactly like the 3D does */
      if (part.role === "sidePanel") {
        pr.sp = { spPick: part.spPick || null, tex: part.tex || null, sw: part.sw || null,
          texWmm: part.texWmm, texHmm: part.texHmm, texRot: part.texRot, texPx: part.texPx, w: part.w, h: part.h };
      }
      push(pr);
      if (Array.isArray(part.holes) && part.holes.length) holesOf(frame, part, b, part.holes, push);
      return;
    }

    if (part.kind === "shapeBand") {
      /* a cut band: the plan region's lateral extent IS its elevation width */
      const pts = flattenPoints(part.region, []);
      if (!pts.length) return;
      const ax = axesOnWall(frame, part.rotY);
      const { u, q } = worldToWall(frame, part.x, part.z);
      let lo = Infinity, hi = -Infinity, qm = -Infinity;
      pts.forEach((pnt) => {
        const lx = pnt[0], lz = pnt[1];
        const uu = u + lx * ax.ux + lz * ax.uz;
        const qq = q + lx * ax.qx + lz * ax.qz;
        lo = Math.min(lo, uu); hi = Math.max(hi, uu); qm = Math.max(qm, qq);
      });
      push({ kind: "rect", cls: classForBox(part, spec), u0: lo, u1: hi, v0: part.y, v1: part.y + (part.h || 0), q: qm, name: part.name, role: part.role });
      return;
    }

    if (part.kind === "doorPivot") {
      const key = "door-" + (fIdx.door++);
      const ax = axesOnWall(frame, part.closedRotY != null ? part.closedRotY : part.rotY);
      const hinge = worldToWall(frame, part.x, part.z);
      const leafW = part.leafW || 0, leafH = part.leafH || 0, leafD = part.leafD || 0.02;
      const centerU = hinge.u + (part.leafOffX || 0) * ax.ux;
      const qLeaf = hinge.q + leafD / 2;
      const v0 = part.y - leafH / 2, v1 = part.y + leafH / 2;
      if (!part.leafGone) {
        /* `hinge`: which EDGE of the leaf carries the hinges, in wall space ("L" = the leaf's
           low-u edge) — the Cabinet Vision hinge glyphs sit on that edge (2026-09-12 CV look) */
        push({ kind: "rect", cls: "elev-front-leaf", u0: centerU - leafW / 2, u1: centerU + leafW / 2, v0, v1, q: qLeaf, name: part.name, role: "door",
          hinge: hinge.u <= centerU ? "L" : "R" });
        /* swing symbol: apex at the hinge edge, base on the latch edge — the CAD convention */
        push({ kind: "swing", cls: "elev-door-swing", hingeU: hinge.u, latchU: 2 * centerU - hinge.u, v0, v1, q: qLeaf + 0.0005, role: "swing" });
        if (Array.isArray(part.leafHoles) && part.leafHoles.length) {
          holesOf(frame, { rotY: part.closedRotY, y: part.y }, { q: qLeaf }, part.leafHoles, push, centerU, ax);
        }
      }
      if (part.handle) {
        const hd = part.handle;
        /* THE 3D's mount rule, verbatim inputs (sceneBuilders doorPivot branch) */
        const hpp = handlePlacePos(handlePlaceFor(vis, key),
          { w: leafW, h: leafH, cx: part.leafOffX || 0, cy: 0, openSign: (part.leafOffX || 0) >= 0 ? 1 : -1 },
          { x: hd.x, y: 0, z: hd.z });
        push(handleRect(ax, hinge, part.y, hpp, hd, qLeaf, (part.name || "door") + " handle"));
      }
      return;
    }

    if (part.kind === "drawerUnit") {
      const key = "drawer-" + (fIdx.drawer++);
      const ax = axesOnWall(frame, part.rotY);
      const unit = worldToWall(frame, part.x, part.z);
      const dFront = (part.children || []).find((c2) => / drawer front$/.test((c2 && c2.name) || ""));
      (part.children || []).forEach((ch) => {
        if (!ch) return;
        const nm = ch.name || "";
        const isFront = / drawer front$/.test(nm);
        const isHandle = / handle$/.test(nm);
        if (!isFront && !isHandle) return;                       // the drawer box is behind the front
        if (isHandle) {
          const pos = dFront
            ? handlePlacePos(handlePlaceFor(vis, key), { w: dFront.w, h: dFront.h, cx: dFront.x, cy: dFront.y, openSign: 0 }, { x: ch.x, y: ch.y, z: ch.z })
            : { x: ch.x, y: ch.y, z: ch.z, rotZ: 0 };
          push(handleRect(ax, unit, part.y, pos, ch, unit.q, nm));
          return;
        }
        const cu = unit.u + (ch.x || 0) * ax.ux + (ch.z || 0) * ax.uz;
        const cq = unit.q + (ch.x || 0) * ax.qx + (ch.z || 0) * ax.qz + (ch.d || 0) / 2;
        const halfU = Math.abs(ax.ux) * (ch.w || 0) / 2 + Math.abs(ax.uz) * (ch.d || 0) / 2;
        const cv = part.y + (ch.y || 0);
        const b = { u0: cu - halfU, u1: cu + halfU, v0: cv - (ch.h || 0) / 2, v1: cv + (ch.h || 0) / 2, q: cq };
        push({ kind: "rect", cls: "elev-front-drawer", ...b, name: nm, role: "drawer" });
        if (Array.isArray(ch.holes) && ch.holes.length) holesOf(frame, { rotY: part.rotY, y: cv }, b, ch.holes, push, cu, ax);
      });
    }
  });

  /* PLINTH ZONE — LEGS + GHOST PLINTH (2026-09-29 user: "the legs that hold the cabinets are
     not showing, nor the plinth… make the legs show and add a ghost mode so you can see both").
     The engine's kickboard marks the zone (base AND tall stand on the platform plinth). The
     carcass box, which the engine runs to the floor BEHIND the board, is drawn standing on its
     feet: the band the board hides was never a visible carcass edge — it read as a thicker
     bottom strip. The legs stand in the zone; the plinth is drawn over them, ghosted. */
  const kickPr = prims.find((pr) => pr.kind === "rect" && pr.role === "kick");
  if (kickPr && kickPr.v1 > kickPr.v0) {
    const zoneTop = kickPr.v1, zoneBot = kickPr.v0;
    const carc = prims.filter((pr) => pr.kind === "rect" && pr.role === "carcass");
    carc.forEach((pr) => { if (pr.v0 < zoneTop - 1e-9 && pr.v1 > zoneTop + 1e-9) pr.v0 = zoneTop; });
    kickPr.ghost = true;
    kickPr.cls = "elev-plinth-rect elev-ghost";
    const u0 = carc.length ? Math.min(...carc.map((c) => c.u0)) : kickPr.u0;
    const u1 = carc.length ? Math.max(...carc.map((c) => c.u1)) : kickPr.u1;
    legPositions(u0, u1).forEach((cu, i) => push({
      kind: "leg", cls: "elev-leg", cu, u0: cu - LEG.socket.w / 2, u1: cu + LEG.socket.w / 2, v0: zoneBot, v1: zoneTop,
      q: kickPr.q - 0.08, name: "leg " + (i + 1), role: "leg", parts: legParts(cu, zoneBot, zoneTop),
    }));
  }

  /* INTERIOR (shelves / trays / pull-outs) as HIDDEN detail — dashed, on top, like a section
     elevation shows what sits behind a closed front. Same layout + anchor the 3D uses
     (sceneBuilders: interiorVisuals placed relative to the box carcass part). */
  if (opts.def && opts.config) {
    const carc = spec.parts.find((pt) => pt && pt.kind === "box" && pt.role === "carcass");
    if (carc) {
      let ivs = [];
      try { ivs = interiorVisuals(opts.def, opts.config, opts.blob || null) || []; } catch (e) { ivs = []; }
      const ax = axesOnWall(frame, carc.rotY);
      const c = worldToWall(frame, carc.x, carc.z);
      ivs.forEach((v) => {
        if (!v) return;
        const cu = c.u + (v.x || 0) * ax.ux + (v.z || 0) * ax.uz;
        const halfU = Math.abs(ax.ux) * (v.w || 0) / 2 + Math.abs(ax.uz) * (v.d || 0) / 2;
        const cv = carc.y + (v.y || 0);
        push({ kind: "hidden", cls: "elev-hidden-line", u0: cu - halfU, u1: cu + halfU, v0: cv - (v.h || 0) / 2, v1: cv + (v.h || 0) / 2, q: 1e6, name: "interior " + (v.kind || ""), role: "interior" });
      });
    }
  }

  prims.sort((a, b) => (a.q - b.q) || (a.i - b.i));
  return prims;
}

/** a handle bar (possibly turned by rotZ) mounted at pos (host-local) on a host at host.{u,q} */
function handleRect(ax, host, hostY, pos, hd, qBase, name) {
  const cu = host.u + (pos.x || 0) * ax.ux;
  const cv = hostY + (pos.y || 0);
  const rz = pos.rotZ || 0, c = Math.abs(Math.cos(rz)), s = Math.abs(Math.sin(rz));
  const w = hd.w || 0, h = hd.h || 0, d = hd.d || 0;
  const halfU = Math.abs(ax.ux) * (c * w / 2 + s * h / 2);
  const halfV = s * w / 2 + c * h / 2;
  return { kind: "rect", cls: "elev-handle-bar", u0: cu - halfU, u1: cu + halfU, v0: cv - halfV, v1: cv + halfV,
    q: qBase + (pos.z || 0) + d / 2, name, role: "handle" };
}

/** face cut-outs (panel-local rectangles) → hole primitives over a front */
function holesOf(frame, part, box, holes, push, centerU, ax) {
  const a = ax || axesOnWall(frame, part.rotY);
  const cu = centerU != null ? centerU : (box.u0 + box.u1) / 2;
  const y = part.y || 0;
  holes.forEach((hh) => {
    if (!hh) return;
    const x1 = Math.min(hh.x1, hh.x2), x2 = Math.max(hh.x1, hh.x2);
    const uA = cu + x1 * a.ux, uB = cu + x2 * a.ux;
    push({ kind: "rect", cls: "elev-cut-hole", u0: Math.min(uA, uB), u1: Math.max(uA, uB),
      v0: y + Math.min(hh.y1, hh.y2), v1: y + Math.max(hh.y1, hh.y2), q: box.q + 0.0002, name: "cut", role: "hole" });
  });
}

/* ── the whole wall ─────────────────────────────────────────────────────── */

/**
 * buildElevationDrawing({ state, lib, blob, wall, walls }) →
 *   { cabinets: Map(placementId → { prims, envelope, role }), fillers: prims[], gola: prims[] }
 *
 * Runs the SAME spec pipeline the 3D view runs (buildCabinetSpecs → applyGolaRuns) and projects
 * everything that stands on `wall`. `envelope` is the module's nominal box (modelEnvelope) — the
 * outline that carries the selection stroke and the drawing number. `fillers` is always empty
 * since 2026-09-15 (auto filler strips are a cut-list, never drawn geometry).
 */
export function buildElevationDrawing({ state, lib, blob, wall, walls }) {
  const out = { cabinets: new Map(), fillers: [], gola: [] };
  const frame = wallFrame(wall, walls || (state && state.walls) || []);
  if (!frame || !state) return out;
  const placements = state.placements || [];
  const byId = new Map(placements.map((p) => [p && p.id, p]));
  const onWall = (id) => { const p = byId.get(id); return !!(p && p.wallId === wall.id); };

  let specs = [];
  try { specs = buildCabinetSpecs(state, lib, blob) || []; } catch (e) { specs = []; }
  specs.forEach((spec) => {
    const p = byId.get(spec.moduleId);
    if (!p || p.wallId !== wall.id) return;
    const def = effDef(p, lib);
    if (!def) return;
    let vis = null;
    try { vis = p.config ? resolveConfigVisual(blob, p.type, p.config) : null; } catch (e) { vis = null; }
    const prims = projectSpec(frame, spec, { def, config: p.config || {}, blob, vis });
    const env = spec.modelEnvelope ? projectBox(frame, spec.modelEnvelope) : null;
    out.cabinets.set(p.id, { prims, envelope: env, role: def.role });
  });

  /* GOLA — the 3D's own post + rail boxes (applyGolaRuns appends "gola-run" specs) */
  try {
    const before = specs.length;
    applyGolaRuns(specs, state, lib, blob);
    specs.slice(before).forEach((gs) => {
      if (!gs || gs.role !== "gola-run") return;
      const members = (gs.golaInfo && gs.golaInfo.members) || [];
      if (!members.some(onWall)) return;
      const mp = byId.get(members.find(onWall));
      const prof = golaProfileById(blob, ((mp && mp.config) || {}).gola) || golaProfileByName(blob, gs.golaInfo && gs.golaInfo.profile);
      const fill = (prof && prof.sw) || null;
      /* `members`: the placements this run serves — the texture render mode paints the
         profile with THEIR gola finish (the same vis the 3D resolves) */
      projectSpec(frame, gs).forEach((pr) => out.gola.push({ ...pr, fill, profile: gs.golaInfo && gs.golaInfo.profile, id: gs.moduleId, members: members.slice() }));
    });
  } catch (e) { /* the gola layer must never break the elevation */ }

  /* AUTO FILLERS ARE NOT DRAWN (2026-09-15 user: "when I put close two cabinets
     a wood shows up — must not happen"). The derived filler cut-list still
     exists for the order payload; the elevation no longer projects it as
     front panels. `fillers` stays an (empty) array for every consumer. */
  return out;
}
